/**
 * RejectionNoticeModal — single-step modal for sending a rejection notice.
 *
 * State machine: loading_profile → idle → sending → success | error
 *
 * Section A — Landlord Contact (all rejection types): pre-populated from profile
 * Section B — Consumer Reporting Agency (CRA basis only)
 * Section C — Credit Score Disclosure (CRA basis + creditScoreUsed checked)
 *
 * Uses createPortal to avoid inert conflicts with ancestor elements.
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { JurisdictionCoverage } from '@resida/api-types/lease-generation';
import { Button } from '../../../../components/ui/button';
import { JurisdictionCoverageStatement } from '../../../../components/JurisdictionCoverageStatement';
import { sendRejectionNotice, REJECTION_BASIS_LABELS, REJECTION_BASIS_NOTICE_TEXT } from '../../../../lib/leasing.api';
import { getManagerProfile } from '../../../../lib/account.api';
import type { ManagerProfile } from '../../../../lib/account.api';

type ModalState =
  | { step: 'loading_profile' }
  | { step: 'idle' }
  | { step: 'sending' }
  | { step: 'success'; subject: string }
  | { step: 'error'; message: string };

export interface RejectionNoticeModalProps {
  leadId: string;
  rejectionBasis: string;
  isOpen: boolean;
  onClose: () => void;
  onNoticeGenerated?: () => void;
  jurisdictionCoverage: JurisdictionCoverage | null;
}

function buildAddressString(profile: ManagerProfile): string {
  const parts: string[] = [];
  const line1Parts = [profile.address1, profile.address2].filter(Boolean);
  if (line1Parts.length > 0) parts.push(line1Parts.join(' '));
  const line2Parts: string[] = [];
  if (profile.city) line2Parts.push(profile.city + ',');
  if (profile.state) line2Parts.push(profile.state);
  if (profile.zipCode) line2Parts.push(profile.zipCode);
  if (line2Parts.length > 0) parts.push(line2Parts.join(' '));
  return parts.join('\n');
}

export function RejectionNoticeModal({
  leadId,
  rejectionBasis,
  isOpen,
  onClose,
  onNoticeGenerated,
  jurisdictionCoverage,
}: RejectionNoticeModalProps) {
  const isCRABasis = rejectionBasis === 'substantive_consumer_report';

  // Landlord contact fields
  const [landlordAddress, setLandlordAddress] = useState('');
  const [landlordPhone, setLandlordPhone] = useState('');
  const [landlordEmail, setLandlordEmail] = useState('');

  // CRA fields
  const [craName, setCraName] = useState('');
  const [craAddress, setCraAddress] = useState('');
  const [craPhone, setCraPhone] = useState('');

  // Credit score fields
  const [creditScoreUsed, setCreditScoreUsed] = useState(false);
  const [creditScore, setCreditScore] = useState('');
  const [creditScoreRange, setCreditScoreRange] = useState('');
  const [creditScoreDate, setCreditScoreDate] = useState('');
  const [creditScoreProvider, setCreditScoreProvider] = useState('');
  const [creditScoreFactors, setCreditScoreFactors] = useState<[string, string, string, string]>(['', '', '', '']);

  const [modalState, setModalState] = useState<ModalState>({ step: 'loading_profile' });
  const dialogRef = useRef<HTMLDivElement>(null);

  // Reset all fields and load profile when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setLandlordAddress('');
    setLandlordPhone('');
    setLandlordEmail('');
    setCraName('');
    setCraAddress('');
    setCraPhone('');
    setCreditScoreUsed(false);
    setCreditScore('');
    setCreditScoreRange('');
    setCreditScoreDate('');
    setCreditScoreProvider('');
    setCreditScoreFactors(['', '', '', '']);
    setModalState({ step: 'loading_profile' });

    void (async () => {
      try {
        const profile = await getManagerProfile();
        setLandlordAddress(buildAddressString(profile));
        setLandlordPhone(profile.phone ?? '');
        setLandlordEmail(profile.email ?? '');
      } catch {
        // Profile fetch failed — let landlord type fields manually
      }
      setModalState({ step: 'idle' });
    })();
  }, [isOpen]);

  // Keyboard focus trap + Escape handler
  useEffect(() => {
    if (!isOpen) return;

    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const dialog = dialogRef.current;
    if (dialog) {
      const first = dialog.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, modalState]);

  const canSend =
    landlordAddress.trim().length > 0 &&
    (
      !isCRABasis || (
        craName.trim().length > 0 &&
        craAddress.trim().length > 0 &&
        craPhone.trim().length > 0 &&
        (!creditScoreUsed || (
          creditScore.trim().length > 0 &&
          creditScoreRange.trim().length > 0 &&
          creditScoreDate.trim().length > 0 &&
          creditScoreProvider.trim().length > 0
        ))
      )
    );

  const handleSend = async () => {
    if (modalState.step !== 'idle') return;
    setModalState({ step: 'sending' });
    try {
      const factors = creditScoreFactors.filter((f) => f.trim().length > 0);
      const response = await sendRejectionNotice(leadId, {
        landlordAddress: landlordAddress.trim() || undefined,
        landlordPhone: landlordPhone.trim() || undefined,
        landlordEmail: landlordEmail.trim() || undefined,
        ...(isCRABasis ? {
          craName: craName.trim() || undefined,
          craAddress: craAddress.trim() || undefined,
          craPhone: craPhone.trim() || undefined,
          creditScoreUsed,
          ...(creditScoreUsed ? {
            creditScore: creditScore.trim() || undefined,
            creditScoreRange: creditScoreRange.trim() || undefined,
            creditScoreDate: creditScoreDate.trim() || undefined,
            creditScoreProvider: creditScoreProvider.trim() || undefined,
            creditScoreFactors: factors.length > 0 ? factors : undefined,
          } : {}),
        } : {}),
      });
      setModalState({ step: 'success', subject: response.subject });
    } catch (err) {
      setModalState({ step: 'error', message: err instanceof Error ? err.message : 'Failed to send notice' });
    }
  };

  const handleDone = () => {
    onNoticeGenerated?.();
    onClose();
  };

  const handleTryAgain = () => {
    setLandlordAddress('');
    setLandlordPhone('');
    setLandlordEmail('');
    setCraName('');
    setCraAddress('');
    setCraPhone('');
    setCreditScoreUsed(false);
    setCreditScore('');
    setCreditScoreRange('');
    setCreditScoreDate('');
    setCreditScoreProvider('');
    setCreditScoreFactors(['', '', '', '']);
    setModalState({ step: 'loading_profile' });

    void (async () => {
      try {
        const profile = await getManagerProfile();
        setLandlordAddress(buildAddressString(profile));
        setLandlordPhone(profile.phone ?? '');
        setLandlordEmail(profile.email ?? '');
      } catch {
        // ignore
      }
      setModalState({ step: 'idle' });
    })();
  };

  const updateFactor = (index: number, value: string) => {
    setCreditScoreFactors((prev) => {
      const next: [string, string, string, string] = [...prev] as [string, string, string, string];
      next[index] = value;
      return next;
    });
  };

  if (!isOpen) return null;

  const inputClass = 'w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring';
  const labelClass = 'block text-sm font-medium mb-1';
  const requiredMark = <span className="text-destructive" aria-hidden="true">*</span>;

  const content = (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
      data-testid="rejection-notice-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rejection-notice-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        className="bg-card rounded-lg border border-border w-full max-w-lg p-6 shadow-lg max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <h2 id="rejection-notice-modal-title" className="text-lg font-semibold">
            Send Rejection Notice
          </h2>
          <button
            type="button"
            data-testid="rejection-notice-close-btn"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors ml-4 shrink-0"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Loading profile */}
        {modalState.step === 'loading_profile' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading your contact information…</p>
          </div>
        )}

        {/* Idle — main form */}
        {modalState.step === 'idle' && (
          <div className="space-y-6">
            {/* Rejection reason summary */}
            <div className="rounded-md bg-muted px-4 py-3 text-sm space-y-1" data-testid="rejection-basis-summary">
              <div>
                <span className="font-medium text-foreground">Basis: </span>
                <span className="text-muted-foreground">
                  {REJECTION_BASIS_LABELS[rejectionBasis as keyof typeof REJECTION_BASIS_LABELS] ?? rejectionBasis}
                </span>
              </div>
              <div>
                <span className="font-medium text-foreground">Notice will state: </span>
                <span className="text-muted-foreground">
                  {REJECTION_BASIS_NOTICE_TEXT[rejectionBasis as keyof typeof REJECTION_BASIS_NOTICE_TEXT] ?? rejectionBasis}
                </span>
              </div>
            </div>

            <JurisdictionCoverageStatement coverage={jurisdictionCoverage} subject="notice" />

            {/* Section A: Landlord Contact */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Your Contact Information</h3>

              <div>
                <label htmlFor="landlord-address" className={labelClass}>
                  Mailing Address {requiredMark}
                </label>
                <textarea
                  id="landlord-address"
                  data-testid="landlord-address-input"
                  value={landlordAddress}
                  onChange={(e) => setLandlordAddress(e.target.value)}
                  placeholder="e.g. 123 Main St&#10;Chicago, IL 60601"
                  rows={3}
                  className={inputClass}
                  required
                  aria-required="true"
                  autoComplete="street-address"
                />
              </div>

              <div>
                <label htmlFor="landlord-phone" className={labelClass}>
                  Phone <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <input
                  id="landlord-phone"
                  type="tel"
                  data-testid="landlord-phone-input"
                  value={landlordPhone}
                  onChange={(e) => setLandlordPhone(e.target.value)}
                  placeholder="e.g. (312) 555-0100"
                  className={inputClass}
                  autoComplete="tel"
                />
              </div>

              <div>
                <label htmlFor="landlord-email" className={labelClass}>
                  Email <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <input
                  id="landlord-email"
                  type="email"
                  data-testid="landlord-email-input"
                  value={landlordEmail}
                  onChange={(e) => setLandlordEmail(e.target.value)}
                  placeholder="e.g. landlord@example.com"
                  className={inputClass}
                  autoComplete="email"
                />
              </div>

              <p className="text-xs text-muted-foreground">
                This information will appear in the notice so the applicant can contact you.
              </p>
            </div>

            {/* Section B: CRA (consumer report basis only) */}
            {isCRABasis && (
              <div className="space-y-4 border-t border-border pt-4">
                <h3 className="text-sm font-semibold text-foreground">Consumer Reporting Agency</h3>

                <p className="text-sm text-muted-foreground">
                  This notice is based on a consumer report. Provide the Consumer Reporting Agency (CRA) details that will appear in the notice.
                </p>

                <div>
                  <label htmlFor="cra-name" className={labelClass}>
                    CRA Name {requiredMark}
                  </label>
                  <input
                    id="cra-name"
                    type="text"
                    data-testid="cra-name-input"
                    value={craName}
                    onChange={(e) => setCraName(e.target.value)}
                    placeholder="e.g. TransUnion, Experian, Equifax"
                    className={inputClass}
                    required
                    aria-required="true"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label htmlFor="cra-address" className={labelClass}>
                    CRA Address {requiredMark}
                  </label>
                  <input
                    id="cra-address"
                    type="text"
                    data-testid="cra-address-input"
                    value={craAddress}
                    onChange={(e) => setCraAddress(e.target.value)}
                    placeholder="e.g. 555 W Adams St, Chicago, IL 60661"
                    className={inputClass}
                    required
                    aria-required="true"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label htmlFor="cra-phone" className={labelClass}>
                    CRA Phone / Toll-free {requiredMark}
                  </label>
                  <input
                    id="cra-phone"
                    type="tel"
                    data-testid="cra-phone-input"
                    value={craPhone}
                    onChange={(e) => setCraPhone(e.target.value)}
                    placeholder="e.g. 1-800-916-8800"
                    className={inputClass}
                    required
                    aria-required="true"
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Required for nationwide CRAs under FCRA § 615(a)(3)(A)
                  </p>
                </div>

                {/* Section C: Credit Score Disclosure */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      id="credit-score-used"
                      type="checkbox"
                      data-testid="credit-score-used-checkbox"
                      checked={creditScoreUsed}
                      onChange={(e) => setCreditScoreUsed(e.target.checked)}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                    />
                    <label htmlFor="credit-score-used" className="text-sm font-medium text-foreground cursor-pointer">
                      A credit score was used in this decision
                    </label>
                  </div>

                  {creditScoreUsed && (
                    <div className="space-y-4 pl-6 border-l-2 border-border">
                      <div>
                        <label htmlFor="credit-score" className={labelClass}>
                          Credit Score {requiredMark}
                        </label>
                        <input
                          id="credit-score"
                          type="text"
                          data-testid="credit-score-input"
                          value={creditScore}
                          onChange={(e) => setCreditScore(e.target.value)}
                          placeholder="e.g. 620"
                          className={inputClass}
                          required
                          aria-required="true"
                        />
                      </div>

                      <div>
                        <label htmlFor="credit-score-range" className={labelClass}>
                          Score Range {requiredMark}
                        </label>
                        <input
                          id="credit-score-range"
                          type="text"
                          data-testid="credit-score-range-input"
                          value={creditScoreRange}
                          onChange={(e) => setCreditScoreRange(e.target.value)}
                          placeholder="e.g. 300–850"
                          className={inputClass}
                          required
                          aria-required="true"
                        />
                      </div>

                      <div>
                        <label htmlFor="credit-score-date" className={labelClass}>
                          Score Date {requiredMark}
                        </label>
                        <input
                          id="credit-score-date"
                          type="text"
                          data-testid="credit-score-date-input"
                          value={creditScoreDate}
                          onChange={(e) => setCreditScoreDate(e.target.value)}
                          placeholder="e.g. 05/15/2025"
                          className={inputClass}
                          required
                          aria-required="true"
                        />
                      </div>

                      <div>
                        <label htmlFor="credit-score-provider" className={labelClass}>
                          Score Provider {requiredMark}
                        </label>
                        <input
                          id="credit-score-provider"
                          type="text"
                          data-testid="credit-score-provider-input"
                          value={creditScoreProvider}
                          onChange={(e) => setCreditScoreProvider(e.target.value)}
                          placeholder="e.g. FICO"
                          className={inputClass}
                          required
                          aria-required="true"
                        />
                      </div>

                      <div className="space-y-2">
                        <p className={labelClass}>Key Factors</p>
                        <p className="text-xs text-muted-foreground">
                          Up to 4 key factors from your screening report (FCRA § 1681m(a)(2))
                        </p>
                        {([0, 1, 2, 3] as const).map((i) => (
                          <input
                            key={i}
                            type="text"
                            data-testid={`credit-score-factor-${i}-input`}
                            value={creditScoreFactors[i]}
                            onChange={(e) => updateFactor(i, e.target.value)}
                            placeholder={`Factor ${i + 1} (optional)`}
                            className={inputClass}
                            aria-label={`Key factor ${i + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2 border-t border-border">
              <Button
                type="button"
                data-testid="rejection-notice-send-btn"
                disabled={!canSend}
                onClick={() => void handleSend()}
                className="flex-1"
              >
                Send Notice
              </Button>
              <Button type="button" variant="outline" data-testid="rejection-notice-cancel-btn" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Sending */}
        {modalState.step === 'sending' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Sending notice…</p>
          </div>
        )}

        {/* Success */}
        {modalState.step === 'success' && (
          <div
            data-testid="rejection-notice-success"
            className="flex flex-col items-center text-center py-4 gap-4"
          >
            <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
              <svg className="w-7 h-7 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Notice sent</h3>
              <p className="text-sm text-muted-foreground">
                "{modalState.subject}" has been sent to the applicant.
              </p>
            </div>
            <Button
              type="button"
              data-testid="rejection-notice-done-btn"
              onClick={handleDone}
              className="w-full"
            >
              Done
            </Button>
          </div>
        )}

        {/* Error */}
        {modalState.step === 'error' && (
          <div className="space-y-4">
            <div
              className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
              role="alert"
            >
              {modalState.message}
            </div>
            <div className="flex gap-3">
              <Button type="button" onClick={handleTryAgain} className="flex-1">
                Try again
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
